import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalBody } from "reactstrap";
import axiosInstance from "../../../axios-instance";
import trueIcon from "../../../assets/images/true-icon.png";
import falseIcon from "../../../assets/images/false-icon.png";
import clear from "../../../assets/images/clear.png";
import ArrowDown from "../../../assets/icons/chevron-double-down.svg";

// ---------------------------------------------------------------------------
// Pure helpers (no React)
// ---------------------------------------------------------------------------

function findItem(plans, planType, planName, featureName) {
  const plan = plans.find(
    (item) => item.plan_type === planType && item.plan_name === planName
  );
  if (!plan) return false;
  const planDetail = plan.plan_details.find(
    (item) => item.feature_name === featureName
  );
  return planDetail ? planDetail.status === "E" : false;
}

// Mobile column feature rows: [enabledCol1, enabledCol2, enabledCol3]
const MOBILE_FEATURE_ROWS = [
  { label: "Calendar integrations with Google Calendar, Office 365, Outlook and iCloud", values: [true, true, true] },
  { label: "Personalize MeetOcto link", values: [true, true, true] },
  { label: "Removable MeetOcto Branding", values: [false, true, true] },
  { label: "Pooled availability options for organizations", small: "(round robin, even rotation, allow attendee to select staff)", values: [false, false, false] },
  { label: "Group Events", values: [false, true, true] },
  { label: "Automated event notifications", values: [false, false, true] },
  { label: "Automated Notifications and Reminders", values: [false, false, true] },
  { label: "Redirect to Custom Pages", values: [false, true, true] },
  { label: "Collect Payments", small: "(PayPal, Stripe)", values: [false, false, true] },
  { label: "Coupon Codes", values: [false, false, true] },
  { label: "Marketing Tools", values: [false, false, true] },
  { label: "Promotional Banners", values: [false, false, false] },
  { label: "Guests", values: [false, false, false] },
  { label: "Upsell/ Cross Sell", values: [false, false, true] },
  { label: "OctoLink Customization", small: "(colors, images upload)", values: [false, true, true] },
  { label: "Metrics and Reporting", values: [true, true, true] },
  { label: "Add OctoLink to your website", values: [true, true, true] },
];

const MOBILE_INTEGRATION_ROWS = [
  { label: "Google Meet", values: [true, true, true] },
  { label: "Zoom", values: [true, true, true] },
  { label: "Microsoft Teams", values: [true, true, true] },
  { label: "Zapier", values: [false, false, true] },
];

// Table feature rows: each cell is { type: 'findItem', planName, featureName } or { type: 'planType', whenIndividual, whenTeam }
const TABLE_PLAN_NAMES = ["Free", "Basic", "Pro"];
const TABLE_FEATURE_ROWS = [
  { name: "Calendar integrations with Google Calendar, Office 365, Outlook and iCloud", cells: ["findItem", "findItem", "findItem"] },
  { name: "Personalize MeetOcto link", cells: ["findItem", "findItem", "findItem"] },
  { name: "Removable MeetOcto Branding", cells: ["planType", "findItem", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Pooled availability options for organizations", nameSpan: "(round robin, even rotation, allow attendee to select staff)", cells: ["planType", "planType", "planType"], cellsPlanType: { whenIndividual: false, whenTeam: true } },
  { name: "Group Events", cells: ["planType", "findItem", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Automated event notifications", cells: ["planType", "planType", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Automated Notifications and Reminders", cells: ["planType", "planType", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Redirect to Custom Pages", cells: ["planType", "findItem", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Collect Payments (PayPal, Stripe)", nameSpan: null, cells: ["planType", "planType", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Coupon Codes", cells: ["planType", "planType", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Marketing Tools", cells: ["planType", "planType", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Promotional Banners", cells: ["planType", "findItem", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Guests", cells: ["planType", "findItem", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Upsell/ Cross Sell", cells: ["planType", "planType", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "OctoLink Customization (colors, images upload)", cells: ["planType", "findItem", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"] },
  { name: "Metrics and Reporting", cells: ["findItem", "findItem", "findItem"] },
  { name: "Add OctoLink to your website", cells: ["findItem", "findItem", "findItem"] },
];

const TABLE_INTEGRATION_ROWS = [
  { name: "Google Meet", cells: ["findItem", "findItem", "findItem"] },
  { name: "Zoom", cells: ["findItem", "findItem", "findItem"] },
  { name: "Microsoft Teams", cells: ["findItem", "findItem", "findItem"] },
  { name: "Zapier", cells: ["planType", "static", "findItem"], cellsPlanType: { whenIndividual: false, whenTeam: true }, findItemPlans: ["Free", "Basic", "Pro"], staticMiddle: true },
];

// ---------------------------------------------------------------------------
// Small presentational components (same file, same DOM/design)
// ---------------------------------------------------------------------------

function FeatureIcon({ enabled, alt = "check icon" }) {
  return (
    <img
      src={enabled ? trueIcon : falseIcon}
      className="plan-tick-icons"
      alt={alt}
    />
  );
}

function PeriodToggle({ planPeriodType, onPeriodChange, variant = "mobile" }) {
  const isMonthly = planPeriodType === "monthly";
  const isAnnual = planPeriodType === "annual";
  if (variant === "mobile") {
    return (
      <div className="btns d-flex">
        <button
          type="button"
          className={`btn annully ${isMonthly ? "btn-app" : "btn-outline"}`}
          onClick={() => onPeriodChange("monthly")}
        >
          Monthly
        </button>
        <button
          type="button"
          className={`btn ml-3 annully ${isAnnual ? "btn-app" : "btn-outline"}`}
          onClick={() => onPeriodChange("annual")}
        >
          Annually
        </button>
      </div>
    );
  }
  return (
    <div className="btns">
      <button
        type="button"
        className={`btn p-3 annully ${isMonthly ? "active" : ""}`}
        onClick={() => onPeriodChange("monthly")}
      >
        Monthly
      </button>
      <button
        type="button"
        className={`btn p-3 ml-3 annully ${isAnnual ? "active" : ""}`}
        onClick={() => onPeriodChange("annual")}
      >
        Annually
      </button>
    </div>
  );
}

function MobilePricingColumn({
  planType,
  planPeriodType,
  onBuyPlan,
  bestValueVisible,
  columnIndex,
  title,
  priceContent,
  buttonClass,
  memberLabel,
  calendarText,
  eventsText,
}) {
  const featureRows = MOBILE_FEATURE_ROWS.map((row, i) => {
    const enabled = row.values[columnIndex];
    const content = (
      <>
        <FeatureIcon enabled={enabled} />
        <span>
          {row.label}
          {row.small != null && <small> {row.small}</small>}
        </span>
      </>
    );
    return <li key={i}>{content}</li>;
  });
  const integrationRows = MOBILE_INTEGRATION_ROWS.map((row, i) => {
    const enabled = row.values[columnIndex];
    return (
      <li key={`int-${i}`}>
        <FeatureIcon enabled={enabled} />
        <span>{row.label}</span>
      </li>
    );
  });

  return (
    <div className="col-lg-4 col-md-6">
      <div className={`pricing_col ${columnIndex === 1 ? "best" : ""}`}>
        <div className="head">
          <div className={`best-plan ${!bestValueVisible ? "op-0" : ""}`}>Best Value</div>
          <h3>{title}</h3>
          {priceContent}
          <button className={`btn ${buttonClass}`} onClick={onBuyPlan}>
            Buy Now
          </button>
        </div>
        <div className="body">
          <ul>
            <li className="feature">
              <span className="full"><b>Features</b></span>
            </li>
            {planType !== "individual" && memberLabel && (
              <li className="feature mt-2">
                <span className="full"><b>{memberLabel}</b></span>
              </li>
            )}
            <li><span className="full">{calendarText}</span></li>
            <li><span className="full">{eventsText}</span></li>
            {featureRows}
            <li><span className="full"><b>Integrations</b></span></li>
            {integrationRows}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TableFeatureCell({
  planType,
  planIndex,
  plans,
  rowConfig,
  isActiveBorder,
}) {
  const planName = TABLE_PLAN_NAMES[planIndex];
  const cellType = Array.isArray(rowConfig.cells)
    ? rowConfig.cells[planIndex]
    : rowConfig.cells[0];
  const pt = rowConfig.cellsPlanType;
  const plansList = rowConfig.findItemPlans || TABLE_PLAN_NAMES;

  let enabled = false;
  if (cellType === "findItem") {
    const name = rowConfig.findItemPlans ? plansList[planIndex] : planName;
    enabled = findItem(plans, "individual", name, rowConfig.name);
  } else if (cellType === "planType" && pt) {
    enabled = planType === "team" ? pt.whenTeam : pt.whenIndividual;
  } else if (cellType === "static" && rowConfig.staticMiddle && planIndex === 1) {
    return (
      <td className={isActiveBorder ? "active-border" : ""}>
        <img src={trueIcon} className="plan-tick-icons" alt="Cross Icon" />
      </td>
    );
  }

  const className = isActiveBorder ? "active-border" : "";
  return (
    <td className={className}>
      <FeatureIcon enabled={enabled} alt="Tick Icon" />
    </td>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function PlanDetailModal({ gotoPaymentPage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planType, setPlanType] = useState("individual");
  const [planPeriodType, setPlanPeriodType] = useState("monthly");

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const setPeriod = useCallback((period) => setPlanPeriodType(period), []);
  const setType = useCallback(() => {
    setPlanType((prev) => (prev === "individual" ? "team" : "individual"));
  }, []);

  useEffect(() => {
    axiosInstance
      .get("/admin/plans")
      .then((response) => setPlans(response.data || []))
      .catch((err) => console.error(err));
  }, []);

  const onBuyPlan = useCallback(() => {
    gotoPaymentPage?.();
    toggle();
  }, [gotoPaymentPage, toggle]);

  const isMonthly = planPeriodType === "monthly";
  const isIndividual = planType === "individual";

  // Mobile column configs: title, priceContent, buttonClass, memberLabel, calendarText, eventsText
  const mobileCol1 = {
    title: isIndividual ? "Free" : "Team",
    priceContent: isIndividual ? <h2>$0</h2> : (
      <>{isMonthly ? <h2>$35</h2> : <h2>$30/month</h2>}</>
    ),
    buttonClass: "btn-outline",
    memberLabel: "2-3 members",
    calendarText: "Connect 1 calendar/user",
    eventsText: "2 Events only",
  };
  const mobileCol2 = {
    title: isIndividual ? "Basic" : "Standard",
    priceContent: (
      <h2>
        {isIndividual && (isMonthly ? "$12" : "$120")}
        {planType === "team" && (isMonthly ? "$55" : "$50/month")}
        <span>/{isMonthly ? "Month" : "Year"}</span>
      </h2>
    ),
    buttonClass: "btn-app",
    memberLabel: "4-5 members",
    calendarText: "Connect 4 calendars/user",
    eventsText: "Unlimited Events",
  };
  const mobileCol3 = {
    title: "Pro",
    priceContent: (
      <h2>
        {isIndividual && (isMonthly ? "$15" : "$156")}
        {planType === "team" && (isMonthly ? "$12/user" : "$10/user")}
        <span>/{isMonthly ? "Month" : "Year"}</span>
      </h2>
    ),
    buttonClass: "btn-outline",
    memberLabel: "6+ members",
    calendarText: "Connect 6 calendars/user",
    eventsText: "Unlimited Events",
  };
  const mobileCols = [mobileCol1, mobileCol2, mobileCol3];

  return (
    <>
      <button type="button" className="btn btn-outline" onClick={toggle}>
        View Details
      </button>

      <Modal
        isOpen={isOpen}
        toggle={toggle}
        className="event-modal modal-xl modal-dialog pricing_table_modal"
      >
        <ModalBody>
          <div className="pricing-modal-container">
            <div className="remove-modal">
              <button type="button" className="btn" onClick={toggle}>
                <img src={clear} alt="" />
              </button>
            </div>
            <div className="pricings-modal">
              <h1 className="heading">Pricing Plans</h1>
              <div className="toggle-section">
                <p>Individual Plans</p>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={planType === "team"}
                    onChange={setType}
                  />
                  <span className="slider round" />
                </label>
                <p>For Organizations</p>
              </div>

              {/* Mobile Columns */}
              <div className="pricing-mobile">
                <PeriodToggle planPeriodType={planPeriodType} onPeriodChange={setPeriod} variant="mobile" />
                <div className="row g-0">
                  {mobileCols.map((col, idx) => (
                    <React.Fragment key={idx}>
                      <MobilePricingColumn
                        planType={planType}
                        planPeriodType={planPeriodType}
                        onBuyPlan={onBuyPlan}
                        bestValueVisible={idx === 1}
                        columnIndex={idx}
                        title={col.title}
                        priceContent={col.priceContent}
                        buttonClass={col.buttonClass}
                        memberLabel={col.memberLabel}
                        calendarText={col.calendarText}
                        eventsText={col.eventsText}
                      />
                      <div className="col-12 text-center pt-4">
                        <span className="full">
                          <img src={ArrowDown} alt="Arrow Down" style={{ width: 50 }} />
                        </span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="table_flow_wrapper">
                <table className="pricing-table">
                    <tr>
                      <th className="names">
                        <div className="chose-plan">
                          <h1>Choose Your Plan</h1>
                          <PeriodToggle planPeriodType={planPeriodType} onPeriodChange={setPeriod} variant="desktop" />
                        </div>
                      </th>
                      <th className="border-top">
                        <div className="plan">
                          <h1>{isIndividual ? <>Free <br /> Simple scheduling</> : <>Team <br />For small teams</>}</h1>
                          {isIndividual ? <h2>$0</h2> : null}
                          {planType === "team" ? (
                            <h2>
                              {isMonthly ? "$12" : "$120"}
                              <span>/{isMonthly ? "Month" : "Year"}</span>
                            </h2>
                          ) : null}
                          <button type="button" className="btn" onClick={onBuyPlan}>
                            Buy Now
                          </button>
                          <h1 className="fill-h">Features</h1>
                          {!isIndividual && <h1 className="fill-h mt-2">2-3 members</h1>}
                        </div>
                      </th>
                      <th className="border-top active-border">
                        <div className="plan active-plan">
                          <div className="active-plan-value">Best Value</div>
                          <h1>{isIndividual ? <>Basic <br />Professional scheduling</> : <>Business <br /> For growing teams</>}</h1>
                          <h2>
                            {isIndividual && (isMonthly ? "$12" : "$120")}
                            {planType === "team" && (isMonthly ? "$15" : "$144")}
                            <span>/{isMonthly ? "Month" : "Year"}</span>
                          </h2>
                          <button type="button" className="btn" onClick={onBuyPlan}>
                            Buy Now
                          </button>
                          <h1 className="fill-h">Features</h1>
                          {!isIndividual && <h1 className="fill-h mt-2">4-5 members</h1>}
                        </div>
                      </th>
                      <th className="border-top">
                        <div className="plan">
                          <h1>{isIndividual ? <>Pro <br /> Advanced scheduling</> : <>Scale <br /> For larger teams</>}  </h1>
                          <h2>
                            {isIndividual && (isMonthly ? "$15" : "$156")}
                            {planType === "team" && (isMonthly ? "$19" : "$192")}
                            <span>/{isMonthly ? "Month" : "Year"}</span>
                          </h2>
                          <button type="button" className="btn" onClick={onBuyPlan}>
                            Buy Now
                          </button>
                          <h1 className="fill-h">Features</h1>
                          {!isIndividual && <h1 className="fill-h mt-2">6+ members</h1>}
                        </div>
                      </th>
                    </tr>
                    <tr>
                      <td className="names">Connected Calendar/s</td>
                      <td className="big-font">1 calendar/user</td>
                      <td className="active-border big-font">4 calendar/user</td>
                      <td className="big-font">6 calendar/user</td>
                    </tr>
                    <tr>
                      <td className="names">Events</td>
                      <td className="big-font">1 only</td>
                      <td className="active-border big-font">Unlimited</td>
                      <td className="big-font">Unlimited</td>
                    </tr>
                    {TABLE_FEATURE_ROWS.map((rowConfig, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className="names">
                          {rowConfig.name}
                          {rowConfig.nameSpan != null && (
                            <span> {rowConfig.nameSpan}</span>
                          )}
                        </td>
                        {[0, 1, 2].map((planIndex) => (
                          <TableFeatureCell
                            key={planIndex}
                            planType={planType}
                            planIndex={planIndex}
                            plans={plans}
                            rowConfig={rowConfig}
                            isActiveBorder={planIndex === 1}
                          />
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td className="names"></td>
                      <td><h1>Integrations</h1></td>
                      <td className="active-border"><h1>Integrations</h1></td>
                      <td><h1>Integrations</h1></td>
                    </tr>
                    {TABLE_INTEGRATION_ROWS.map((rowConfig, rowIdx) => (
                      <tr key={`int-${rowIdx}`}>
                        <td className="names">{rowConfig.name}</td>
                        {[0, 1, 2].map((planIndex) => (
                          <TableFeatureCell
                            key={planIndex}
                            planType={planType}
                            planIndex={planIndex}
                            plans={plans}
                            rowConfig={rowConfig}
                            isActiveBorder={planIndex === 1}
                          />
                        ))}
                      </tr>
                    ))}
                </table>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default PlanDetailModal;
